import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Briefcase, GitBranch, Sparkles, Settings, LogOut, Bell } from 'lucide-react';

import { useAuth } from '../auth/AuthContext.jsx';
import { api } from '../api/client.js';
import './Layout.css';

const NAV_ITEMS = [
    { to: '/pipeline',   label: 'Pipeline',   Icon: Briefcase },
    { to: '/deals',      label: 'Deals',      Icon: GitBranch },
    { to: '/matchmaker', label: 'Matchmaker', Icon: Sparkles  },
    { to: '/settings',   label: 'Settings',   Icon: Settings  },
];

export function Layout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const isStaff = user?.role === 'admin' || user?.role === 'team';

    // Pending access requests badge (admin/team only)
    const { data: pending } = useQuery({
        queryKey: ['access-requests-pending'],
        queryFn: () => api.get('/access-requests/pending'),
        enabled: isStaff,
        refetchInterval: 60_000,  // poll every minute
    });

    const handleLogout = () => {
        logout();
        navigate('/login', { replace: true });
    };

    return (
        <div className="layout">
            <aside className="sidebar">
                <div className="brand">
                    <img src="/behold-logo-white.png" alt="Behold" className="brand-logo" />
                    <div className="brand-text">
                        <div className="brand-name display">Behold</div>
                        <div className="brand-sub">Investor Pipeline</div>
                    </div>
                </div>

                <nav className="nav">
                    {NAV_ITEMS.map(({ to, label, Icon }) => (
                        <NavLink
                            key={to}
                            to={to}
                            className={({ isActive }) => `nav-item${isActive ? ' is-active' : ''}`}
                        >
                            <Icon size={16} strokeWidth={1.75} />
                            <span>{label}</span>
                        </NavLink>
                    ))}

                    {isStaff && pending?.count > 0 && (
                        <NavLink
                            to="/access-requests"
                            className={({ isActive }) => `nav-item nav-item-alert${isActive ? ' is-active' : ''}`}
                        >
                            <Bell size={16} strokeWidth={1.75} />
                            <span>Access requests</span>
                            <span className="nav-badge">{pending.count}</span>
                        </NavLink>
                    )}
                </nav>

                <div className="sidebar-footer">
                    <div className="user-card">
                        <div className="user-avatar">
                            {(user?.name || user?.email || '?').charAt(0).toUpperCase()}
                        </div>
                        <div className="user-info">
                            <div className="user-name">{user?.name || user?.email}</div>
                            <div className="user-role">{user?.role}</div>
                        </div>
                        <button className="user-logout" onClick={handleLogout} title="Sign out">
                            <LogOut size={14} strokeWidth={1.75} />
                        </button>
                    </div>
                </div>
            </aside>

            <main className="main">
                <Outlet />
            </main>
        </div>
    );
}
